import { useRef } from 'react';
import { useTaskRequest } from '../../api/hooks/useTaskRequest';
import type { TaskState } from '../../api/rmf-client';
import { useSeenOnce } from '../../utils/use-seen-once';
import { locationFromPhases, locationFromRequest } from './task-location';

/**
 * One row's Location cell.
 *
 * The phases answer first and cost nothing. Only a row they say nothing
 * for -- a queued job, which publishes no phases -- asks for its
 * dispatch request, and only once the row has been on screen
 * (`useSeenOnce`): the list holds a thousand rows and a read per row on
 * open would be a thousand requests.
 *
 * NEVER AN ERROR IN THE CELL. A 404 is the ordinary answer for a job
 * RMF started itself (a charge retreat, an idle park) and for a dispatch
 * whose reply was lost; a failure is one cell of hundreds. Either way
 * the honest cell is the same dash as "no place on record", and the
 * drawer is where the reason is spelled out.
 */
export function TaskLocationCell({ task }: { task: TaskState }) {
  const ref = useRef<HTMLSpanElement>(null);
  const fromPhases = locationFromPhases(task);
  const seen = useSeenOnce(ref, fromPhases === null);
  const request = useTaskRequest(fromPhases === null && seen ? task.id : null, {
    background: true,
  });
  const record = fromPhases === null ? request.data : null;
  const location =
    fromPhases ??
    (record ? locationFromRequest(record.request?.category ?? task.type, record.request) : null);

  // One element whatever it says, so the element being watched is the
  // one on screen for as long as it is being watched.
  const pending = !location && request.isLoading;
  return (
    <span
      ref={ref}
      className={pending ? 'nx-tasks__cell-pending' : undefined}
      title={location && location.full !== location.text ? location.full : undefined}
    >
      {location ? location.text : pending ? '…' : '—'}
    </span>
  );
}
